interface FilterJsonValue {
	type: string
	value: string[]
}

type FilterJson = Record<string, FilterJsonValue>

interface FilterProperty {
	CODE: string
	VALUES: Array<{
		ID?: string
		VALUE: string | number
		FILTERED_VALUE?: string | number
	}>
}

interface BuildFilterSefUrlParams {
	baseUrl: string
	filterJson: string
	properties: FilterProperty[]
	sortParam?: string
}

/**
 * Универсальная функция формирования ЧПУ для фильтров каталога
 * Используется в новинках, брендах, уценке, дискаунте
 *
 * @example
 * buildFilterSefUrl({
 *   baseUrl: '/new/airpods/',
 *   filterJson: '{"color":{"type":"=","value":["black"]}}',
 *   properties: filterData.PROPERTIES,
 *   sortParam: 'popular'
 * })
 * // => '/new/airpods/filter/color-is-123/?sort=popular'
 */
export const buildFilterSefUrl = ({ baseUrl, filterJson, properties, sortParam }: BuildFilterSefUrlParams): string => {
	const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`

	const isEmptyFilter = !filterJson || filterJson === '{}'
	if (isEmptyFilter) {
		return appendSortParam(normalizedBaseUrl, sortParam)
	}

	const filterObj: FilterJson = JSON.parse(filterJson)
	const filterParts = buildFilterParts(filterObj, properties)

	if (filterParts.length === 0) {
		return appendSortParam(normalizedBaseUrl, sortParam)
	}

	const sefUrl = `${normalizedBaseUrl}filter/${filterParts.join('/')}/`
	return appendSortParam(sefUrl, sortParam)
}

function buildFilterParts(filterObj: FilterJson, properties: FilterProperty[]): string[] {
	const parts: string[] = []

	for (const filterKey of Object.keys(filterObj)) {
		if (filterKey === 'LIST_SECTION_CODE') continue

		const property = properties.find(prop => prop.CODE === filterKey)
		if (!property) continue

		const filterValues = filterObj[filterKey].value
		const valueIds = findValueIds(filterValues, property)

		if (valueIds.length > 0) {
			const valuesStr = valueIds.length === 1 ? valueIds[0] : valueIds.join('-or-')
			parts.push(`${filterKey.toLowerCase()}-is-${valuesStr}`)
		}
	}

	return parts
}

function findValueIds(filterValues: string[], property: FilterProperty): string[] {
	const ids: string[] = []

	for (const value of filterValues) {
		const filterValue = property.VALUES.find(v => {
			const filteredValue = v.FILTERED_VALUE || v.VALUE
			return String(filteredValue) === String(value)
		})

		if (filterValue?.ID) {
			ids.push(filterValue.ID)
		}
	}

	return ids
}

function appendSortParam(url: string, sortParam?: string): string {
	if (!sortParam) return url

	const separator = url.includes('?') ? '&' : '?'
	return `${url}${separator}sort=${sortParam}`
}
